
import { Card, CardContent } from "@/components/ui/card";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertCircle, WifiOff, ServerCrash } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface ResourcesErrorProps {
  message?: string;
  onRetry?: () => void;
  showNotification?: boolean;
  autoRetry?: boolean;
  isFirebaseError?: boolean;
}

export const ResourcesError = ({ 
  message = "Error loading resources", 
  onRetry,
  showNotification = false,
  autoRetry = false,
  isFirebaseError = false
}: ResourcesErrorProps) => {
  const { toast } = useToast();
  const [retryCount, setRetryCount] = useState(0);
  const [retryCountdown, setRetryCountdown] = useState(5);
  const [isAutoRetrying, setIsAutoRetrying] = useState(autoRetry);
  
  // Only show notification on first render
  useEffect(() => {
    if (showNotification) {
      toast({
        title: "Connection Error",
        description: message,
        variant: "destructive",
      });
    }
  }, []);
  
  // Handle auto retry logic
  useEffect(() => {
    let retryTimeout: number;
    let countdownInterval: number;
    
    if (isAutoRetrying && onRetry) {
      // Set up countdown
      countdownInterval = window.setInterval(() => {
        setRetryCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Set up retry
      retryTimeout = window.setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setRetryCountdown(5);
        onRetry();
      }, 5000);
    }
    
    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [isAutoRetrying, onRetry, retryCount]);

  const handleManualRetry = () => {
    setRetryCount(prev => prev + 1);
    setRetryCountdown(5);
    if (onRetry) onRetry();
  };

  const toggleAutoRetry = () => {
    setIsAutoRetrying(prev => !prev);
    setRetryCountdown(5);
  };

  const getFirebaseErrorHelp = () => {
    if (!isFirebaseError) return null;
    
    return (
      <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800">
        <h4 className="font-medium mb-1">Connection Issue Detected</h4>
        <p className="mb-2">
          There seems to be a problem with the connection for this interactive content.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Check your internet connection</li>
          <li>Try disabling any content/ad blockers</li>
          <li>Clear your browser cache and cookies</li>
          <li>Try using a different browser</li>
        </ul>
      </div>
    );
  };

  const getErrorIcon = () => {
    if (isFirebaseError) {
      return <WifiOff className="h-12 w-12 text-red-500" />;
    }
    
    return <AlertCircle className="h-12 w-12 text-red-500" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 pt-24">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <div className="flex justify-center mb-4">
                {getErrorIcon()}
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                {message}
              </h3>
              <p className="text-gray-500 mb-4">
                {onRetry ? "Please try again." : "Please try refreshing the page."}
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3">
                {onRetry ? (
                  <>
                    <Button 
                      onClick={handleManualRetry} 
                      className="flex items-center gap-2 w-full sm:w-auto"
                      disabled={retryCount > 10}
                    >
                      <RefreshCw className="h-4 w-4" />
                      Try Again {retryCount > 0 ? `(${retryCount})` : ''}
                    </Button>
                    <Button 
                      onClick={toggleAutoRetry} 
                      variant={isAutoRetrying ? "destructive" : "outline"}
                      className="w-full sm:w-auto mt-2 sm:mt-0"
                    >
                      {isAutoRetrying ? "Stop Auto-Retry" : "Enable Auto-Retry"}
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => window.location.reload()} className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Refresh Page
                  </Button>
                )}
              </div>
              {isAutoRetrying && onRetry && (
                <p className="text-sm text-gray-400 mt-2">
                  Automatically retrying in {retryCountdown} seconds...
                </p>
              )}
              {retryCount > 10 && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-100 rounded-lg">
                  <p className="text-sm text-amber-600">
                    Multiple retry attempts have failed. There might be a persistent issue with your connection or browser settings.
                  </p>
                  <Button 
                    onClick={() => window.location.reload()} 
                    variant="outline" 
                    className="mt-2 bg-white"
                  >
                    Refresh Page Instead
                  </Button>
                </div>
              )}
              {getFirebaseErrorHelp()}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
